import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useArticles } from "@/hooks/useArticles";

const categoryColors: Record<string, { bg: string; text: string; dot: string }> = {
  "Success Story":      { bg: "bg-orange-50",  text: "text-orange-600",  dot: "bg-orange-500" },
  "Startup":            { bg: "bg-green-50",   text: "text-green-700",   dot: "bg-green-500" },
  "Innovation":         { bg: "bg-yellow-50",  text: "text-yellow-700",  dot: "bg-yellow-500" },
  "Femme Entrepreneuse":{ bg: "bg-pink-50",    text: "text-pink-600",    dot: "bg-pink-500" },
  "Jeune Talent":       { bg: "bg-violet-50",  text: "text-violet-600",  dot: "bg-violet-500" },
  "Diaspora Africaine": { bg: "bg-amber-50",   text: "text-amber-700",   dot: "bg-amber-500" },
};

function formatDate(dateStr: string, lang: string) {
  const d = new Date(dateStr);
  return d.toLocaleDateString(lang === "fr" ? "fr-FR" : "en-US", { day: "numeric", month: "short", year: "numeric" });
}

function isRecent(dateStr: string) {
  const d = new Date(dateStr);
  const cutoff = new Date("2024-01-01");
  return d >= cutoff;
}

export default function TrendingSection() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith("fr") ? "fr" : "en";
  const [activeTab, setActiveTab] = useState(0);
  const { articles, loading } = useArticles({ limit: 30 });

  const tabs = [
    { labelKey: "trending.recent", icon: "ri-time-line", sort: "recent" },
    { labelKey: "trending.popular", icon: "ri-fire-line", sort: "views" },
  ];

  const sorted = [...articles].sort((a, b) => {
    if (activeTab === 0) return new Date(b.date).getTime() - new Date(a.date).getTime();
    return (b.views ?? 0) - (a.views ?? 0);
  });

  const featured = sorted[0];
  const list = sorted.slice(1, 7);

  const colors = (cat: string) => categoryColors[cat] ?? { bg: "bg-gray-50", text: "text-gray-600", dot: "bg-gray-400" };

  return (
    <section className="py-16 bg-white border-b border-gray-100">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <div>
            <div className="w-10 h-1 rounded-full bg-orange-500 mb-3"></div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 font-serif">
              {t("trending.title")}
            </h2>
            <p className="text-gray-500 mt-1 text-sm">{t("trending.subtitle")}</p>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1 bg-gray-100 rounded-full p-1 self-start sm:self-auto">
            {tabs.map((tab, i) => (
              <button
                key={tab.sort}
                onClick={() => setActiveTab(i)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  activeTab === i
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <i className={`${tab.icon} text-base ${activeTab === i ? "text-orange-500" : ""}`}></i>
                {t(tab.labelKey)}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            <div className="lg:col-span-3 bg-gray-100 rounded-2xl h-[420px] animate-pulse"></div>
            <div className="lg:col-span-2 flex flex-col gap-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex gap-3">
                  <div className="w-20 h-20 bg-gray-100 rounded-xl animate-pulse flex-shrink-0"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-3 bg-gray-100 rounded animate-pulse w-1/3"></div>
                    <div className="h-4 bg-gray-100 rounded animate-pulse"></div>
                    <div className="h-3 bg-gray-100 rounded animate-pulse w-2/3"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-8">

            {/* Featured card */}
            {featured && (
              <Link
                to={`/article/${featured.id}`}
                className="lg:col-span-3 group relative rounded-2xl overflow-hidden cursor-pointer block"
                style={{ minHeight: "420px" }}
              >
                <img
                  src={featured.image}
                  alt={featured.title}
                  className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/50 to-black/10"></div>

                <div className="absolute top-4 left-4 flex items-center gap-2 flex-wrap">
                  <span className={`text-xs font-bold px-3 py-1 rounded-full ${colors(featured.category).bg} ${colors(featured.category).text}`}>
                    {featured.category}
                  </span>
                  {isRecent(featured.date) && (
                    <span className="flex items-center gap-1 bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                      <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
                      {t("trending.recent.badge")}
                    </span>
                  )}
                </div>

                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="flex items-center gap-2 text-orange-400 text-xs mb-3">
                    <span>{featured.countryFlag}</span>
                    <span>{featured.country}</span>
                    <span className="opacity-50">·</span>
                    <span>{featured.sector}</span>
                    <span className="opacity-50">·</span>
                    <i className="ri-calendar-line"></i>
                    <span>{formatDate(featured.date, lang)}</span>
                  </div>
                  <h3 className="text-white font-bold text-xl md:text-2xl leading-tight line-clamp-2 mb-3 group-hover:text-orange-300 transition-colors">
                    {featured.title}
                  </h3>
                  <p className="text-white/65 text-sm line-clamp-2 mb-5">{featured.summary}</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-orange-500/20 flex items-center justify-center">
                        <i className="ri-user-line text-orange-400 text-sm"></i>
                      </div>
                      <div>
                        <p className="text-white text-xs font-semibold">{featured.entrepreneur}</p>
                        <p className="text-white/40 text-xs">{featured.source}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {featured.views ? (
                        <span className="text-white/50 text-xs flex items-center gap-1">
                          <i className="ri-eye-line"></i>
                          {featured.views >= 1000 ? `${(featured.views / 1000).toFixed(0)}K` : featured.views}
                        </span>
                      ) : null}
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-orange-500 text-white group-hover:bg-orange-400 transition-colors">
                        <i className="ri-arrow-right-line"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            )}

            {/* List */}
            <div className="lg:col-span-2 flex flex-col gap-1">
              {list.map((article, idx) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group flex items-start gap-4 p-3 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <span className="text-2xl font-black text-gray-100 group-hover:text-orange-200 transition-colors w-7 flex-shrink-0 leading-none mt-1 select-none">
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                  <div className="w-20 h-20 flex-shrink-0 rounded-xl overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${colors(article.category).bg} ${colors(article.category).text}`}>
                        {article.category}
                      </span>
                      {isRecent(article.date) && (
                        <span className="flex items-center gap-1 text-orange-500 text-xs font-semibold">
                          <span className={`w-1.5 h-1.5 rounded-full ${colors(article.category).dot}`}></span>
                          {new Date(article.date).getFullYear()}
                        </span>
                      )}
                    </div>
                    <h4 className="text-sm font-bold text-gray-900 line-clamp-2 leading-snug group-hover:text-orange-500 transition-colors mb-1">
                      {article.title}
                    </h4>
                    <div className="flex items-center gap-2 text-xs text-gray-400">
                      <span>{article.countryFlag} {article.country}</span>
                      {article.views ? (
                        <>
                          <span>·</span>
                          <span className="flex items-center gap-0.5">
                            <i className="ri-eye-line"></i>
                            {article.views >= 1000 ? `${(article.views / 1000).toFixed(0)}K` : article.views}
                          </span>
                        </>
                      ) : (
                        <>
                          <span>·</span>
                          <span>{formatDate(article.date, lang)}</span>
                        </>
                      )}
                    </div>
                  </div>
                </Link>
              ))}

              <Link
                to="/listing"
                className="mt-3 flex items-center justify-center gap-2 border border-gray-200 hover:border-orange-300 hover:bg-orange-50 text-gray-600 hover:text-orange-600 text-sm font-semibold py-3 rounded-xl transition-all duration-200 cursor-pointer"
              >
                {t("trending.viewAll")}
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
